/* eslint-disable import/extensions */
import { Feature } from '../common'
import { PAGES } from '../../constants/GLOBAL_VARIABLE'

const AUTH_SELECTOR_SCHEME_NAME = 'nexonconsoleauth'
const AUTH_SELECTOR_SCHEME_PATH_FOR_NEXON_LOGIN = 'nexonlogin'
const AUTH_SELECTOR_SCHEME_PATH_FOR_SIGNIN_RESULT = 'signinresult'

export default class PlatformSignin extends Feature {
  constructor({ mainUrl, authSelector = [] }) {
    super({ namespace: 'ial', enumKey: 'IAL.PLATFORM_SIGNIN' })
    this.mainUrl = mainUrl || 'https://signin.nexon.com'

    const {
      // Common
      type,
      redirect_uri: redirectUri,
      applicationid: applicationId, // From GS SDK

      // From Game
      gid,
      support_platforms: supportPlatforms,
      with_popup_code: withPopupCode, // ncs | ...
      use_guest_session: useGuestSession, // true

      // From INSIGN-Front
      signed_in_by: signedInBy, // nexon | steam | xbox | psn
      web_ticket: webTicket,
    } = this.queryParam

    this.redirectUri = redirectUri

    this.gid = gid
    this.type = type
    this.supportPlatforms = supportPlatforms
    this.authSelector = authSelector
    this.withPopupCode = withPopupCode
    this.useGuestSession = useGuestSession
    this.applicationId = applicationId

    this.signedInBy = signedInBy
    this.webTicket = webTicket

    console.debug('[PlatformSignin Instance]', JSON.stringify(this, null, 2))
  }

  get isSignedIn() {
    return !!this.signedInBy
  }

  get isMobile() {
    return this.type === 'ingame-mobile'
  }

  get responseParams() {
    return {
      signed_in_by: this.signedInBy,
      ...(this.webTicket ? { web_ticket: this.webTicket } : {}),
    }
  }

  /**
   * NOTE. (24. 12. 13 기준)
   *
   * support_platforms 쿠키 설정 우선순위
   *
   * - 1순위 쿼리 파라미터 (?support_platforms=)
   * - 2순위 INFACE Console 인증 셀렉터 설정 값 (auth_selector)
   * - 3순위 1,2순위 모두 미설정 시 에러 페이지 노출 (insign-front에서 처리)
   */
  get supportPlatformsByOrder() {
    if (this.supportPlatforms) {
      return this.supportPlatforms
    }

    if (this.authSelector.length) {
      return this.authSelector.join(',')
    }

    return undefined
  }

  setCookie() {
    const cookieData = {
      gid: this.gid,
      type: this.type,
      support_platforms: this.supportPlatformsByOrder,
      redirect_uri: this.redirectUri,
      with_popup_code: this.withPopupCode,
      use_guest_session: this.useGuestSession,
      applicationid: this.applicationId,
    }
    this.namespaceCookie.setCookie(cookieData)
  }

  saveReferrer() {
    const redirect = this.redirectUri ? decodeURIComponent(this.redirectUri) : ''

    if (!(redirect || document.referrer)) return

    const url = new URL(redirect || document.referrer)
    const referrer = url.toString()
    this.redirectUri = referrer
  }

  redirectToGame() {
    const url = new URL(this.redirectUri)
    const params = new URLSearchParams({
      ...Object.fromEntries(url.searchParams), // NOTE: 최초 연동처에서 전달된 파라미터들을 보존합니다.
      ...this.responseParams,
    })

    url.search = params.toString()

    this.redirect(url)
  }

  redirectToMobile() {
    const scheme = `${AUTH_SELECTOR_SCHEME_NAME}.${this.applicationId}://`

    let redirectUrl = scheme
    const isMobileNexonSigninRequired = this.signedInBy === 'nexon'

    if (isMobileNexonSigninRequired) {
      redirectUrl += AUTH_SELECTOR_SCHEME_PATH_FOR_NEXON_LOGIN
    } else {
      redirectUrl += AUTH_SELECTOR_SCHEME_PATH_FOR_SIGNIN_RESULT

      const queryParams = Object.keys(this.responseParams).map((key) => {
        const value = this.responseParams[key]
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      }).join('&')

      if (queryParams) {
        redirectUrl += `?${queryParams}`
      }
    }

    console.warn('[redirectToMobile]', redirectUrl.toString())

    /**
     * NOTE. (24. 12. 16 기준)
     *
     * 스킴 이동 시 loation.replace을 사용하게 되면 모바일에서 확인 팝업이 뜨는 이슈가 있습니다.
     * 따라서, 반드시 location.href를 사용하여 이동합니다.
     */
    // window.location.href = redirectUrl
    this.navigateToUrlWithoutHistory(redirectUrl)
  }

  gotoAuthSelectorPage() {
    this.navigateToUrlWithoutHistory(`${this.mainUrl}/${PAGES.IAL_AUTH_SELECTOR}`)
  }

  gotoAuthSelectorExternalPage() {
    this.navigateToUrlWithoutHistory(`${this.mainUrl}/${PAGES.IAL_AUTH_SELECTOR_EXTERNAL}`)
  }

  gotoAuthSelectorMobilePage() {
    this.navigateToUrlWithoutHistory(`${this.mainUrl}/${PAGES.IAL_AUTH_SELECTOR_MOBILE}`)
  }

  #processAfterSignin() {
    window.$showLoginLoading()
    if (this.isMobile) {
      this.redirectToMobile()
    } else {
      this.redirectToGame()
    }
  }

  #preprocessBeforeSignin() {
    this.saveReferrer()
    this.setCookie()
  }

  #processBeforeSignin({ externalOnly }) {
    if (externalOnly) {
      this.gotoAuthSelectorExternalPage()
    } else if (this.isMobile) {
      this.gotoAuthSelectorMobilePage()
    } else {
      this.gotoAuthSelectorPage()
    }
  }

  processAuthSelector({ externalOnly }) {
    if (this.isSignedIn) {
      this.#processAfterSignin()
    } else {
      this.#preprocessBeforeSignin()
      this.#processBeforeSignin({ externalOnly })
    }
  }
}
